import { Box, Divider, Loader, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DownloadFileButton } from 'src/common/components/buttons/DownloadFileButton';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { useCalendarEventMeetingAgenda } from 'src/meetings/hooks/useCalendarEventMeetingAgenda';
import { useGenerateCalendarEventMeetingAgenda } from 'src/meetings/hooks/useGenerateCalendarEventMeetingAgenda';
import type { CalendarEvent } from 'src/meetings/models';
import { getFirstClientParticipant, getParticipantFullName } from '../utils/event-utils';
import { Logger } from 'src/utils/Logger';
import { GradientBorderedInfoCard } from 'src/common/components/card/GradientBorderedInfoCard';
import { ErrorCard } from 'src/meetings/components/ErrorCard';

type Props = {
  event: CalendarEvent;
};

export const MeetingAgendaCard = ({ event }: Props) => {
  const { files: templates } = useTemplates({
    templateType: 'MEETING_AGENDA',
  });

  const {
    generateMeetingAgenda,
    isLoading: isGenerating,
    isError: isErrorGeneratingMeetingAgenda,
    isSuccess: isGenerateMeetingAgendaSuccess,
  } = useGenerateCalendarEventMeetingAgenda(event.id);

  const {
    meetingAgenda,
    isLoading: isLoadingAgenda,
    refetch,
  } = useCalendarEventMeetingAgenda({
    eventId: event.id,
    enabled: event.hasMeetingAgenda || isGenerateMeetingAgendaSuccess,
  });

  const defaultTemplate = templates.length > 0 ? templates[0] : null;

  const isBackgroundProcessing =
    meetingAgenda?.backgroundProcess?.status === 'PENDING' ||
    meetingAgenda?.backgroundProcess?.status === 'PROCESSING';

  const isSuccess = meetingAgenda?.backgroundProcess?.status === 'SUCCESS';

  const handleGenerate = async () => {
    if (!defaultTemplate) return;

    try {
      await generateMeetingAgenda({ templateId: defaultTemplate.id });
      refetch();
    } catch (error) {
      Logger.error('Failed to generate meeting agenda', error, {
        eventId: event.id,
        templateId: defaultTemplate.id,
      });
    }
  };

  if (defaultTemplate == null) {
    return null;
  }

  if (isSuccess && meetingAgenda?.markdown) {
    return (
      <Box style={{ position: 'relative' }}>
        <Box
          style={{
            position: 'absolute',
            bottom: 'var(--mantine-spacing-sm)',
            left: 'var(--mantine-spacing-md)',
            zIndex: 1,
          }}
        >
          <DownloadFileButton variant="outline-black" bg="white" fileId={meetingAgenda.fileId!} />
        </Box>
        <MeetingAgendaContainer>
          <Divider color="gray.2" />
          <Box mah={550} style={{ overflowY: 'scroll', overflowX: 'hidden' }} pr="xs">
            <MarkdownEditor
              rootClass="compact"
              defaultValue={meetingAgenda.markdown}
              readonly
              h="100%"
              bg="transparent"
              p={0}
              bd="none"
              m="-xs"
            />
          </Box>
        </MeetingAgendaContainer>
      </Box>
    );
  }

  const isGeneratingMeetingAgenda = isBackgroundProcessing || isGenerating || isLoadingAgenda;
  const isError =
    isErrorGeneratingMeetingAgenda || meetingAgenda?.backgroundProcess?.status === 'FAILED';

  return (
    <MeetingAgendaContainer>
      <GenerateMeetingAgendaSection
        isGeneratingMeetingAgenda={isGeneratingMeetingAgenda}
        onGenerateClick={handleGenerate}
        isErrorGeneratingMeetingAgenda={isError}
        event={event}
      />
    </MeetingAgendaContainer>
  );
};

type ContainerProps = {
  children: React.ReactNode;
};

function MeetingAgendaContainer({ children }: ContainerProps) {
  return (
    <Box bg="gray.1" p="lg" style={{ borderRadius: '8px' }}>
      <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="sm">
        Meeting agenda
      </Text>
      {children}
    </Box>
  );
}

type GenerateProps = {
  isGeneratingMeetingAgenda: boolean;
  isErrorGeneratingMeetingAgenda: boolean;
  onGenerateClick: () => unknown;
  event: CalendarEvent;
};

function GenerateMeetingAgendaSection({
  isGeneratingMeetingAgenda,
  isErrorGeneratingMeetingAgenda,
  onGenerateClick,
  event,
}: GenerateProps) {
  const clientParticipant = getFirstClientParticipant(event);
  const clientName = getParticipantFullName(clientParticipant) ?? 'the client';

  if (isGeneratingMeetingAgenda) {
    return (
      <GradientBorderedInfoCard bg={'gray.1'} leftIcon={<Loader size="24px" />}>
        Generating meeting agenda
      </GradientBorderedInfoCard>
    );
  }

  if (isErrorGeneratingMeetingAgenda) {
    return (
      <ErrorCard bg={'gray.1'}>
        <Text ml={'xxs'} size="sm" fw={FONT_WEIGHTS.semibold}>
          Unable to generate the meeting agenda, please{' '}
          <Text
            span
            c={'brand.8'}
            size="sm"
            fw={600}
            style={{ cursor: 'pointer' }}
            onClick={onGenerateClick}
          >
            try again
          </Text>
        </Text>
      </ErrorCard>
    );
  }

  return (
    <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
      <Text component="span" c="brand.6" style={{ cursor: 'pointer' }} onClick={onGenerateClick}>
        Generate a personalised agenda
      </Text>{' '}
      based on {clientName}&apos;s profile
    </Text>
  );
}
